import { useEffect, useRef, useState } from 'react'

import { clampPercent } from './arcGeometry'

// Quick enough to feel responsive, slow enough to read as a fill.
export const FILL_DURATION_MS = 1000
// Later changes start from where the arc already sits, so they need less time.
export const UPDATE_DURATION_MS = 400

// Decelerating fill: fast off the mark, easing into the final value.
const easeOutCubic = progress => 1 - (1 - progress) ** 3

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * Eases a 0–100 percentage in from zero on mount, then from the current
 * position on later changes. Returns the target unchanged when animation is
 * off or the user prefers reduced motion, so an opted-out caller renders a
 * changed target on the same paint rather than one behind.
 */
const useAnimatedPercent = (target, animate = false) => {
  const percent = clampPercent(target)
  // Checked once; an OS-level change mid-session is not worth re-reading.
  const [reduceMotion] = useState(prefersReducedMotion)
  const enabled = animate && !reduceMotion

  // Holds the animated value while animation is on, and null while it is off
  // so nothing stale outlives a change to `enabled`.
  const [value, setValue] = useState(enabled ? 0 : null)
  // Mirrors the rendered value so a retargeted animation starts from it.
  const valueRef = useRef(enabled ? 0 : percent)
  const hasFilledRef = useRef(false)
  const [wasEnabled, setWasEnabled] = useState(enabled)

  // Re-seed during render, not in an effect, so neither path ever paints a
  // value the other left behind. React re-runs this component before painting.
  if (wasEnabled !== enabled) {
    setWasEnabled(enabled)
    // Turning animation on picks up from the arc's current length, so it
    // eases from there on the next change instead of refilling from zero.
    setValue(enabled ? percent : null)
    valueRef.current = percent
  }

  useEffect(() => {
    if (!enabled) {
      // Keeps the baseline current for a later change to `enabled`.
      valueRef.current = percent
      return undefined
    }

    const from = valueRef.current
    if (from === percent) return undefined

    const duration = hasFilledRef.current ? UPDATE_DURATION_MS : FILL_DURATION_MS
    hasFilledRef.current = true

    let frame
    let startTime

    const step = now => {
      if (startTime === undefined) {
        startTime = now
      }

      const progress = duration > 0 ? Math.min((now - startTime) / duration, 1) : 1
      // Progress of 1 eases to exactly 1, so the arc lands on the target.
      valueRef.current = from + (percent - from) * easeOutCubic(progress)
      setValue(valueRef.current)

      if (progress < 1) {
        frame = requestAnimationFrame(step)
      }
    }

    frame = requestAnimationFrame(step)
    return () => cancelAnimationFrame(frame)
  }, [enabled, percent])

  // Never read the state while animation is off: it seeds on mount only, so
  // it would render a changed target one paint late.
  if (!enabled) return percent

  // `value` is null only on the render that re-seeds it, which never paints.
  return value ?? percent
}

export default useAnimatedPercent
